import React, { useState, useEffect } from 'react';
import env from '../../config';

const API_ROOT = env.backendUrl && env.backendUrl.endsWith('/api') ? env.backendUrl.replace(/\/$/, '') : `${env.backendUrl.replace(/\/$/, '')}/api`;
const API_URL = `${API_ROOT}/dashboard`;

export default function Dashboard() {
  // compute today's date in local YYYY-MM-DD
  const today = (() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  })();

  const [tasks, setTasks] = useState([]);
  const [formData, setFormData] = useState({ id: null, data: today, pessoa: '', tempo: '', descricao: '' });
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL, { headers: { Accept: 'application/json' } });
      const json = await res.json();
      setTasks(Array.isArray(json) ? json : []);
    } catch (err) {
      console.error('fetchTasks error', err);
      setError('Erro ao carregar tarefas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Totals per pessoa
  const totals = React.useMemo(() => {
    let felipe = 0;
    let miguel = 0;
    tasks.forEach((t) => {
      const tempo = Number(t.tempo) || 0;
      if ((t.pessoa || '').toLowerCase() === 'felipe') felipe += tempo;
      if ((t.pessoa || '').toLowerCase() === 'miguel') miguel += tempo;
    });
    return { felipe, miguel, total: felipe + miguel };
  }, [tasks]);

  const formatMinutes = (mins) => {
    const m = Number(mins) || 0;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    if (h === 0 && mm === 0) return '0h';
    if (h === 0) return `${mm}m`;
    return mm === 0 ? `${h}h` : `${h}h ${mm}m`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: name === 'tempo' ? (value === '' ? '' : Number(value)) : value }));
  };

  const handleEdit = (task) => {
    setFormData({ id: task.id, data: task.data || today, pessoa: task.pessoa || '', tempo: task.tempo || '', descricao: task.descricao || '' });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFormData({ id: null, data: today, pessoa: '', tempo: '', descricao: '' });
    setIsEditing(false);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = isEditing ? 'PUT' : 'POST';
      const url = isEditing ? `${API_URL}/${formData.id}` : API_URL;
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ data: formData.data, pessoa: formData.pessoa, tempo: formData.tempo, descricao: formData.descricao }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || 'Erro na requisição');
      }
      // reload
      await fetchTasks();
      handleCancel();
    } catch (err) {
      console.error('submit error', err);
      setError('Erro ao salvar. Verifique os campos.');
    }
  };

  const handleDelete = async (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Confirmar exclusão?')) return;
    try {
      const res = await fetch(`${API_URL}/${id}`, { method: 'DELETE', headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('Erro ao deletar');
      setTasks((t) => t.filter((x) => x.id !== id));
    } catch (err) {
      console.error('delete error', err);
      setError('Erro ao excluir');
    }
  };

  if (loading) return <div className="p-8 text-center text-lg text-gray-600">Carregando...</div>;

  return (
    <div className="container mx-auto p-4 md:p-8">
      <h1 className="text-2xl font-bold mb-4">Dashboard — CRUD de Tarefas</h1>

      <div className="bg-white p-4 rounded shadow mb-6">
        <h2 className="font-semibold text-lg mb-2">{isEditing ? 'Editar tarefa' : 'Nova tarefa'}</h2>
        {error && <div className="text-red-600 mb-2">{error}</div>}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input name="data" type="date" value={formData.data} onChange={handleInputChange} className="p-2 border rounded" required />
          <select name="pessoa" value={formData.pessoa} onChange={handleInputChange} className="p-2 border rounded" required>
            <option value="">Selecione pessoa</option>
            <option value="felipe">Felipe</option>
            <option value="miguel">Miguel</option>
          </select>
          <input name="tempo" type="number" value={formData.tempo} onChange={handleInputChange} placeholder="Tempo (min)" className="p-2 border rounded" required />
          <input name="descricao" type="text" value={formData.descricao} onChange={handleInputChange} placeholder="Descrição" className="p-2 border rounded md:col-span-4" />

          <div className="md:col-span-4 flex justify-end space-x-2 mt-2">
            {isEditing && (
              <button type="button" onClick={handleCancel} className="px-4 py-2 bg-gray-200 rounded">Cancelar</button>
            )}
            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded">{isEditing ? 'Salvar' : 'Criar'}</button>
          </div>
        </form>
      </div>
      {/* Totals per pessoa */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded shadow">
          <div className="text-sm text-gray-500">Felipe</div>
          <div className="text-xl font-semibold">{formatMinutes(totals.felipe)}</div>
        </div>
        <div className="bg-white p-4 rounded shadow">
          <div className="text-sm text-gray-500">Miguel</div>
          <div className="text-xl font-semibold">{formatMinutes(totals.miguel)}</div>
        </div>
      </div>
      <div className="bg-white p-4 rounded shadow">
        <h2 className="font-semibold text-lg mb-2">Lista</h2>
        {tasks.length === 0 ? (
          <div className="text-gray-500">Nenhuma tarefa encontrada.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs text-gray-500">ID</th>
                  <th className="px-4 py-2 text-left text-xs text-gray-500">Data</th>
                  <th className="px-4 py-2 text-left text-xs text-gray-500">Pessoa</th>
                  <th className="px-4 py-2 text-left text-xs text-gray-500">Tempo</th>
                  <th className="px-4 py-2 text-left text-xs text-gray-500">Descrição</th>
                  <th className="px-4 py-2 text-right text-xs text-gray-500">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {tasks.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm">{t.id}</td>
                    <td className="px-4 py-2 text-sm">{t.data}</td>
                    <td className="px-4 py-2 text-sm">{t.pessoa}</td>
                    <td className="px-4 py-2 text-sm">{t.tempo}</td>
                    <td className="px-4 py-2 text-sm max-w-xs truncate">{t.descricao}</td>
                    <td className="px-4 py-2 text-sm text-right">
                      <button onClick={() => handleEdit(t)} className="text-indigo-600 mr-3">Editar</button>
                      <button onClick={() => handleDelete(t.id)} className="text-red-600">Excluir</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
